import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import Contact from './components/Contact';
import Servers from './components/Servers';
import AddServers from './components/AddServers';
import ServerDetails from './components/ServerDetails';
import { Routes, Route, useNavigate } from 'react-router-dom';
import AuthCallback from './components/AuthCallback';
import DangerZone from './components/DangerZone';
import { getMe, logout as apiLogout } from './lib/api';
import { API_BASE } from './lib/config';

// Lightweight JWT decoder (no verification, just base64 decode)
function decodeJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('JWT Decode Error:', e);
    return null;
  }
}

function TopBarAuth({ user, profile, loading, onLogout, setMenuOpen, menuOpen }) {
  const loginUrl = `${API_BASE}/v1/auth/steam/login`;

  // Close menu on click outside
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (!e.target.closest('.auth-menu')) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, setMenuOpen]);

  if (!user) {
    return (
      <a className="steam-login-btn" href={loginUrl} title="Sign in with Steam" aria-label="Sign in with Steam">
        <span>Sign in with Steam</span>
      </a>
    );
  }

  const displayName = (profile && profile.name) || user.steamId;
  const avatarUrl = profile && profile.avatar;

  return (
    <div className="auth-status">
      <div className="auth-text auth-menu">
        {/* Avatar button opens menu */}
          <strong>{displayName}</strong>

          <button type="button" className="auth-avatar-btn" onClick={() => setMenuOpen(v => !v)} aria-haspopup="menu" aria-expanded={menuOpen} title={displayName}>
          {avatarUrl ? (
            <img className="auth-avatar" src={avatarUrl} alt="Avatar" />
          ) : (
            <span className="auth-avatar placeholder" aria-hidden="true">👤</span>
          )}
        </button>

        {menuOpen && (
          <ul className="dropdown-menu dropdown-right" role="menu" style={{minWidth: 200}}>
            <li role="none"><a href="#" role="menuitem" onClick={(e) => { e.preventDefault(); onLogout(); }}>Logout</a></li>
          </ul>
        )}
      </div>
    </div>
  );
}

function TopBar({ isSearching, authProps }) {
  return (
      <header className="topbar" role="banner">
          <div className="topbar-logo">
              <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Logo" style={{height: '100%'}}/>
          </div>
          <div className="topbar-auth" role="complementary">
            <TopBarAuth {...authProps} />
          </div>
          {isSearching && (
            <div className="loader-container" aria-hidden="true">
                <div className="loader-bar grid-style" />
            </div>
          )}
      </header>
  );
}

function Sidebar({ current, onChange }) {
  const items = [
    { key: 'servers', label: 'Servers', iconClass: 'fa-solid fa-server' },
    // Use custom image icon for Danger Zone
    { key: 'danger', label: 'Danger Zone', imgSrc: process.env.PUBLIC_URL + '/dz_icon.png' },
    { key: 'add', label: 'Add Servers', iconClass: 'fa-solid fa-plus' },
  ];
  return (
    <aside className="sidebar" aria-label="Main navigation">
      <div className="nav">
        {items.map(it => (
          <button
            key={it.key}
            className={`nav-item ${current === it.key ? 'active' : ''}`}
            title={it.label}
            onClick={() => onChange(it.key)}
          >
            {it.imgSrc ? (
              <img className="nav-icon" src={it.imgSrc} alt="" aria-hidden="true" />
            ) : (
              <i className={`nav-icon ${it.iconClass}`} aria-hidden="true" />
            )}
            <span className="sr-only">{it.label}</span>
          </button>
        ))}
      </div>
    </aside>
  );
}

function HomeViews({ onDZSearchingChange, authReady }) {
  const [view, setView] = useState('servers');
  const [selectedIp, setSelectedIp] = useState(null);

  const setViewWrapped = (v) => {
    setView(v);
    if (v !== 'danger' && typeof onDZSearchingChange === 'function') {
      onDZSearchingChange(false);
    }
  };

  const openDetails = (ip) => {
    setSelectedIp(ip);
    setViewWrapped('details');
  };

  const backToServers = () => {
    setViewWrapped('servers');
  };

  // If auth is not ready (still checking token), we might want to wait?
  // But if authReady means "we have finished initial check", then proceed.
  if (!authReady) {
      return <div className="app-shell"><main className="main"><div className="loader-bar" /></main></div>;
  }

  return (
    <div className="app-shell">
      <Sidebar current={view} onChange={setViewWrapped} />
      <main className="main">
        {view === 'servers' && <Servers onShowDetails={openDetails} />}
        {view === 'details' && (
          <>
            {selectedIp ? (
              <div>
                <ServerDetails ip={selectedIp} onBack={backToServers} />
              </div>
            ) : (
              <div className="container"><div className="grid-state">No server selected.</div></div>
            )}
          </>
        )}
        {view === 'add' && (<>
          <Servers onShowDetails={openDetails} />
          <AddServers modalOnly onClose={() => setViewWrapped('servers')} />
        </>)}
        {view === 'danger' && <DangerZone onSearchingChange={onDZSearchingChange} />}
        {view === 'contact' && <Contact />}
      </main>
    </div>
  );
}

function App() {
  const [isSearchingDZ, setIsSearchingDZ] = useState(false);

  const [access, setAccess] = useState(() => localStorage.getItem('access_token'));
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(!!localStorage.getItem('access_token'));
  const [menuOpen, setMenuOpen] = useState(false);
  const [authReady, setAuthReady] = useState(!localStorage.getItem('access_token'));

  useEffect(() => {
    const handleAuthChange = (key, newValue) => {
      if (key === 'access_token') {
        setAccess(newValue);
        if (newValue) {
           setLoadingProfile(true);
           setAuthReady(false);
        } else {
           setProfile(null);
           setLoadingProfile(false);
           setAuthReady(true);
        }
      }
      if (key === 'logout_trigger') {
        setAccess(null);
        setProfile(null);
        setMenuOpen(false);
        setAuthReady(true);
      }
    };

    const onStorage = (e) => {
      handleAuthChange(e.key, e.newValue);
    };

    const onCustomAuth = (e) => {
      handleAuthChange(e.detail.key, e.detail.newValue);
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('auth-change', onCustomAuth);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth-change', onCustomAuth);
    };
  }, []);

  const profileFetchOnce = useRef(0);
  useEffect(() => { profileFetchOnce.current = 0; }, [access]);

  useEffect(() => {
    let aborted = false;
    async function fetchProfile() {
      if (!access) {
        setProfile(null);
        setAuthReady(true);
        setLoadingProfile(false);
        return;
      }
      if (profileFetchOnce.current > 0) return;
      profileFetchOnce.current += 1;

      setLoadingProfile(true);
      
      try {
        const res = await getMe();
        if (!res.ok) {
          setProfile(null);
        } else {
          const data = await res.json();
          if (!aborted) {
            setProfile({ name: data.name, avatar: data.avatar, points: data.points });
          }
        }
      } catch (e) {
        if (!aborted) setProfile(null);
      } finally {
        if (!aborted) {
            setLoadingProfile(false);
            setAuthReady(true);
        }
      }
    }
    fetchProfile();
    return () => { aborted = true; };
  }, [access]);

  const user = useMemo(() => {
    if (!access) return null;
    const payload = decodeJwt(access);
    if (!payload) return null;
    const steamId = payload.sub || payload.id || payload.steamid || payload.identity;
    return steamId ? { steamId: String(steamId) } : null;
  }, [access]);

  const handleLogout = () => {
    try { apiLogout(); } catch (_) {}
    setAccess(null);
    setProfile(null);
    setMenuOpen(false);
    setAuthReady(true);
  };

  const authProps = {
      user,
      profile,
      loading: loadingProfile,
      onLogout: handleLogout,
      menuOpen,
      setMenuOpen
  };

  return (
    <>
      <div className="bg-image" aria-hidden="true" style={{ backgroundImage: "url('/background.jpg')" }} />
      <div className="bg-overlay" aria-hidden="true" />

      <TopBar isSearching={isSearchingDZ} authProps={authProps} />

      <Routes>
        <Route path="/" element={<HomeViews onDZSearchingChange={setIsSearchingDZ} authReady={authReady} />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
      </Routes>

      <div className="corner-footer" aria-label="Site notice">
          <div>Not affiliated with Valve and Steam.</div>
          <div>Copyright © 2025-2025 CS2Servers.</div>
      </div>
    </>
  );
}

export default App;
